import React from 'react';
import { View, Pressable } from 'react-native-web';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomText from '../../components/atoms/CustomText';
import { colors, spacing, typography } from '../../utils/styling';
import { useResponsive } from '../../hooks/useResponsive';
import SvgChevron from '../../components/icons/SvgChevron';

interface SpecificResultsData {
  answers: number[];
  specificCategoryScores: Record<string, number>;
  dominantSpecificCategory: string;
  clientId?: number;
  sfAccount?: string | null;
  quizId?: number;
  mainResultsData?: {
    answers: number[];
    categoryScores: Record<string, number>;
    dominantCategory: string;
    clientId?: number;
    sfAccount?: string | null;
    quizId?: number;
  };
}

export default function ConfirmConnect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const [showReturn, setShowReturn] = React.useState(false);
  const resultsData = location.state as SpecificResultsData;

  const handleBack = () => {
    // Navigate back to specific results page
    navigate('/quiz/specific/results', { state: { ...resultsData, from: location.pathname } });
  };

  const handleYes = () => {   // Navigate to 'priorities' pages
    console.log('User selected Yes - connect with team member');
    navigate('/quiz/connect/submit', { state: { ...resultsData, from: location.pathname } });
  };

  const handleNo = () => {  // Show return button
    console.log('User selected No - do not connect');
    setShowReturn(true);
  };

  const handleReturn = () => {  // Navigate back to specific results
    console.log("User is returning back to specific results");
    navigate('/quiz/specific/results', { state: { ...resultsData, from: location.pathname } });
  }

  return (
    <View style={{ 
      width: '100%', 
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: isMobile ? spacing.md : spacing.xl,
      paddingLeft: isMobile ? spacing.lg : spacing.xxl, 
      paddingRight: isMobile ? spacing.lg : spacing.xxl, 
      marginBottom: spacing.xxl,
    }}>
      <View
        style={{
          backgroundColor: colors.white,
          borderRadius: 16,
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
          padding: isMobile ? spacing.md : spacing.xl,
          maxWidth: 900,
          width: "100%",
          marginBottom: spacing.xxl,
        }}
      >
        {/* Back Button */}
        <Pressable
          onPress={handleBack}
          style={{
            padding: spacing.sm,
            cursor: 'pointer',
            flexDirection: 'row',
          }}
        >
          <View style={{ transform: 'rotate(90deg)' }}>
            <SvgChevron 
              color={colors.gray[600]} 
              width={isMobile ? 20 : 24} 
              height={isMobile ? 20 : 24}
            />
          </View>
        </Pressable>

        {/* Content */}
        <View style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          marginTop: isMobile ? spacing.lg : spacing.xl,
        }}>
          {/* Main Question */}
          <CustomText style={{
            fontSize: isMobile ? 36 : 48,
            fontWeight: '700',
            color: colors.dark,
            fontFamily: "Nunito",
            textAlign: 'center',
            marginBottom: isMobile ? spacing.lg : spacing.xl,
            maxWidth: '90%',
          }}>
            {!showReturn ? "Would you like more information or to speak with a team member?" : "Thank you for taking our Money Personality Assessment."}
          </CustomText>

          {/* Subtitle */}
          <CustomText style={{
            fontSize: isMobile ? 18 : 24,
            color: colors.gray[600],
            fontFamily: typography.fontFamily.body,
            textAlign: 'center',
            marginBottom: isMobile ? spacing.xxl : spacing.xxl * 1.5,
          }}>
            {!showReturn ? "85% of people found more information helpful." : "To see your specific results, please click the button below."}
          </CustomText>

          {/* Buttons */}
          <View style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            gap: isMobile ? spacing.md : spacing.lg,
            width: '100%',
            maxWidth: 400,
          }}>
            {!showReturn ? (
            <>
              <Pressable 
                onPress={handleYes}
                style={({ hovered }: { hovered: boolean }) => ({
                  backgroundColor: hovered ? '#515A64' : colors.dark,
                  paddingVertical: isMobile ? spacing.sm : spacing.md,
                  paddingHorizontal: isMobile ? spacing.xl : spacing.xxl,
                  borderRadius: 12,
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                })}
              >
                <CustomText style={{
                  color: colors.white,
                  fontSize: isMobile ? 18 : 24,
                  fontWeight: '600',
                  fontFamily: typography.fontFamily.body,
                }}>
                  Yes
                </CustomText>
              </Pressable>
              <Pressable
                onPress={handleNo}
                style={({ hovered }: { hovered: boolean }) => ({
                  backgroundColor: hovered ? colors.gray[400] : colors.gray[200],
                  paddingVertical: isMobile ? spacing.sm : spacing.md,
                  paddingHorizontal: isMobile ? spacing.xl : spacing.xxl,
                  borderRadius: 12,
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                })}
              >
                <CustomText style={{
                  color: 'black',
                  fontSize: isMobile ? 18 : 24,
                  fontWeight: '500',
                  fontFamily: typography.fontFamily.body,
                }}>
                  No
                </CustomText>
              </Pressable>
            </>
            ) : (
              <Pressable 
              onPress={handleReturn}
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: hovered ? '#515A64' : colors.dark,
                paddingVertical: isMobile ? spacing.sm : spacing.md,
                paddingHorizontal: isMobile ? spacing.xl : spacing.xxl,
                borderRadius: 12,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              })}
            >
              <CustomText style={{
                color: colors.white,
                fontSize: isMobile ? 18 : 24,
                fontWeight: '600',
                fontFamily: typography.fontFamily.body,
              }}>
                Return to my results
              </CustomText>
            </Pressable>
            )}
          </View>
        </View>
      </View>
    </View>
  );
}
